/**
 * Resources that live on somebody's server, not in this folder.
 *
 * `dependency 'ox_lib'` is not a broken reference. It is the single most common
 * line in a modern FiveM resource, and the framework it names is installed once
 * per server and shared by everything on it. The same goes for `es_extended`,
 * `qb-core`, `oxmysql` and the rest of the short list every roleplay server runs.
 *
 * A checker that does not know this reports a correct resource as broken, and
 * the author learns to ignore the report. That is the failure mode this table
 * exists to prevent: not a missed warning, but a warning nobody reads.
 *
 * What is *not* here matters too. This is an allowlist of things known to be
 * external, not a claim about what is installed — an unknown name still resolves
 * to `missing`, because "I have never heard of this" and "this is definitely
 * fine" are different answers and the user deserves the honest one.
 *
 * Casing follows each project's own spelling. `PolyZone` and `polyzone` are the
 * same resource written two ways, and both must resolve; the normalized map
 * below is what does that, and the display name is what a finding prints.
 */

/**
 * Canonical spellings, sorted by the framework family they belong to rather than
 * alphabetically, because that is how someone reads the list to check whether
 * their stack is covered.
 */
export const FRAMEWORK_RESOURCES: readonly string[] = [
  // ESX
  "es_extended",
  "esx_addonaccount",
  "esx_addoninventory",
  "esx_menu_default",
  "esx_menu_dialog",
  "esx_menu_list",
  "esx_society",

  // QBCore and QBox
  "qb-core",
  "qbx_core",
  "qb-input",
  "qb-inventory",
  "qb-management",
  "qb-menu",
  "qb-phone",
  "qb-policejob",
  "qb-target",
  "qb-vehiclekeys",
  "qb-weapons",

  // Overextended
  "ox_lib",
  "ox_doorlock",
  "ox_fuel",
  "ox_inventory",
  "ox_target",
  "oxmysql",

  // Databases
  "ghmattimysql",
  "mysql-async",

  // Voice
  "mumble-voip",
  "pma-voice",

  // UI and interaction
  "menuv",
  "nh-context",
  "warmenu",
  "interact-sound",
  "screenshot-basic",

  // Zones, appearance and world
  "PolyZone",
  "bob74_ipl",
  "fivem-appearance",
  "illenium-appearance",

  // Phones
  "lb-phone",
  "npwd",

  // Other frameworks and bridges
  "ND_Core",
  "rsg-core",
  "vrp",
  "wasabi_bridge",
  "redem_roleplay",

  // Utility
  "cron",
  "instance",
];

/**
 * Lower-cased name → canonical spelling.
 *
 * Built from the list above rather than written out, so adding a framework is a
 * one-line change and the two can never drift apart.
 */
const BY_NORMALIZED = new Map(
  FRAMEWORK_RESOURCES.map((name) => [name.toLowerCase(), name] as const),
);

export function isFrameworkResource(name: string): boolean {
  return BY_NORMALIZED.has(name.trim().toLowerCase());
}

/** The project's own spelling of a known framework, or `null`. */
export function frameworkResourceName(name: string): string | null {
  return BY_NORMALIZED.get(name.trim().toLowerCase()) ?? null;
}

/**
 * Which framework family a resource belongs to, for the "your stack is ESX but
 * this resource wants QBCore" note that is worth far more than a bare
 * "dependency not found".
 */
export type FrameworkFamily = "esx" | "qbcore" | "qbox" | "ox" | "nd" | "rsg" | "vrp" | "other";

const FAMILY_PREFIXES: readonly (readonly [string, FrameworkFamily])[] = [
  ["es_extended", "esx"],
  ["esx_", "esx"],
  ["qbx_", "qbox"],
  ["qb-", "qbcore"],
  ["ox_", "ox"],
  ["oxmysql", "ox"],
  ["nd_", "nd"],
  ["rsg-", "rsg"],
  ["vrp", "vrp"],
];

export function frameworkFamily(name: string): FrameworkFamily {
  const normalized = name.trim().toLowerCase();
  for (const [prefix, family] of FAMILY_PREFIXES) {
    if (normalized === prefix || normalized.startsWith(prefix)) return family;
  }
  return "other";
}
